import { TransactionView } from "@common/types/views/transactionViews";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    DEFAULT_FILTERS,
    TransactionsFilters,
} from "@components/transactions/filters/lib";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import classNames from "classnames";
import { useContext } from "react";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { TransactionActionsToggleFilter } from "@components/transactions/filters/TransactionActionsToggleFilter";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import styles from "./TransactionSearchDrawer.module.scss";

export interface TransactionSearchDrawerContentProps {
    view: TransactionView;
    open: boolean;
    onClose: () => void;
    parentFilters?: TransactionsFilters;
    cacheKey?: WORKSPACE_USER_CACHE_KEYS;
    emptyView?: () => React.ReactNode;
    pinnedRows?: TransactionListItem[];
    showEntityFilter?: boolean;
    showActionsFilter?: boolean;
    addon?: React.ReactNode;
    contentClassName?: string;
}

export const TransactionSearchDrawerContent: React.FC<
    TransactionSearchDrawerContentProps
> = ({
    emptyView,
    pinnedRows,
    showEntityFilter = true,
    showActionsFilter = true,
    addon,
    contentClassName,
}) => {
    const { currentView, filters } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const updateFilters = (filterUpdate: TransactionsFilters) => {
        setCurrentFilters({ ...filters, ...filterUpdate });
    };

    const isAccrualLedgerFilterEnabled = useIsAccrualLedgerEnabled();
    const { enableSimilarityRules } = useTypedFlags();

    return (
        <>
            {addon}
            <TablePageHeader
                search={filters.search}
                onSearchChange={(value) => {
                    updateFilters({ search: value ?? "" });
                }}
                filters={filters}
                onFiltersChange={updateFilters}
                defaultFilters={DEFAULT_FILTERS}
                hideClearButton
                availableFilters={[
                    FilterTabs.DATE,
                    FilterTabs.CATEGORY,
                    FilterTabs.ACCOUNT,
                    FilterTabs.AMOUNT,
                    FilterTabs.RULES,
                    ...(enableSimilarityRules ? [FilterTabs.SIMILAR_TO] : []),
                    FilterTabs.COUNTERPARTY,
                    FilterTabs.NOTE,
                    FilterTabs.RECEIPTS,
                    FilterTabs.SPLIT,
                    FilterTabs.MATCHED,
                    FilterTabs.LEDGER,
                    ...(isAccrualLedgerFilterEnabled
                        ? [FilterTabs.ACCRUAL_LEDGER]
                        : []),
                ]}
                isFullPage
                borderTop={Boolean(addon)}
            >
                {showEntityFilter && (
                    <EntityFilterCombobox
                        filters={filters}
                        onChange={updateFilters}
                        excludeEmptyMockEntities={
                            ExcludeEmptyMockEntitiesMode.REQUIRE_TRANSACTIONS
                        }
                    />
                )}

                {showActionsFilter && (
                    <TransactionActionsToggleFilter
                        filters={filters}
                        onChange={updateFilters}
                    />
                )}
            </TablePageHeader>
            <div className={classNames(styles.content, contentClassName)}>
                <TransactionsView
                    view={currentView}
                    emptyView={emptyView}
                    pinnedRows={pinnedRows}
                />
            </div>
        </>
    );
};
